import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Plus, Target, TrendingUp, Users, Eye, Heart, CheckCircle } from 'lucide-react';

const CorporateDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [myIntents, setMyIntents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('intents'); // 'intents', 'projects', 'matches'

  useEffect(() => {
    checkAuth();
  }, [navigate]);

  const checkAuth = async () => {
    const token = localStorage.getItem('xchange_token');
    const userData = localStorage.getItem('xchange_user');

    if (!token || !userData) {
      navigate('/xchange/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'corporate') {
      navigate('/xchange/login');
      return;
    }

    setUser(parsedUser);
    await fetchDashboardData(token);
  };

  const fetchDashboardData = async (token) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

      // Fetch my intents
      const intentsRes = await fetch(`${backendUrl}/api/xchange/intents/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const intentsData = await intentsRes.json();
      setMyIntents(intentsData);

      // Fetch all projects
      const projectsRes = await fetch(`${backendUrl}/api/xchange/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const projectsData = await projectsRes.json();
      setProjects(projectsData);

      // Fetch my matches
      const matchesRes = await fetch(`${backendUrl}/api/xchange/matches/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const matchesData = await matchesRes.json();
      setMatches(matchesData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const handleCreateIntent = () => {
    // Navigate to intent creation form (to be implemented)
    alert('Intent creation form will be implemented');
  };

  const handleExpressInterest = async (projectId) => {
    try {
      const token = localStorage.getItem('xchange_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/xchange/interest/express`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ projectId })
      });

      if (response.ok) {
        alert('Interest expressed successfully! We will notify you if there is mutual interest.');
        // Refresh data
        await fetchDashboardData(token);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to express interest');
      }
    } catch (error) {
      console.error('Error expressing interest:', error);
      alert('An error occurred while expressing interest');
    }
  };

  const getMatchStatusBadge = (status) => {
    const styles = {
      pending_admin: 'bg-yellow-100 text-yellow-800',
      connected: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      pending_admin: 'Pending Admin Review',
      connected: 'Connected',
      closed: 'Closed'
    };
    return { style: styles[status] || 'bg-gray-100 text-gray-800', label: labels[status] || status };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{borderColor: '#14B8A6'}}></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Section */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{color: '#1E3A5F'}}>
                Corporate Dashboard
              </h1>
              <p className="text-gray-600">Welcome back, {user?.orgName}</p>
            </div>
            <button
              onClick={() => navigate('/saas/sponsor/dashboard')}
              className="px-6 py-3 rounded-lg font-medium transition-all hover:shadow-lg"
              style={{
                background: '#14B8A6',
                color: '#FFFFFF'
              }}
            >
              View Project Monitoring →
            </button>
          </div>

          {/* Quick Access: Project Management Hub */}
          <div className="mb-8 bg-gradient-to-r from-[#14B8A6] to-[#0D9488] rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Project Management</h3>
                <p className="text-white/90 text-sm mb-4">
                  Track funded projects, monitor milestones, and review impact reports in real-time
                </p>
                <button
                  onClick={() => navigate('/project-management')}
                  className="px-6 py-2 bg-white rounded-lg font-semibold hover:shadow-lg transition-all"
                  style={{color: '#14B8A6'}}
                >
                  Access Project Hub →
                </button>
              </div>
              <div className="hidden md:block">
                <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center">
                  <TrendingUp className="w-12 h-12 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(30, 58, 95, 0.1)'}}>
                  <Target className="w-6 h-6" style={{color: '#1E3A5F'}} />
                </div>
              </div>
              <div className="text-3xl font-bold mb-1" style={{color: '#1E3A5F'}}>{myIntents.length}</div>
              <div className="text-sm text-gray-600">My Intents</div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(20, 184, 166, 0.1)'}}>
                  <Eye className="w-6 h-6" style={{color: '#14B8A6'}} />
                </div>
              </div>
              <div className="text-3xl font-bold mb-1" style={{color: '#1E3A5F'}}>{projects.length}</div>
              <div className="text-sm text-gray-600">Available Projects</div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(42, 131, 120, 0.1)'}}>
                  <Heart className="w-6 h-6" style={{color: '#2A8378'}} />
                </div>
              </div>
              <div className="text-3xl font-bold mb-1" style={{color: '#1E3A5F'}}>{matches.length}</div>
              <div className="text-sm text-gray-600">Total Matches</div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(20, 184, 166, 0.1)'}}>
                  <CheckCircle className="w-6 h-6" style={{color: '#14B8A6'}} />
                </div>
              </div>
              <div className="text-3xl font-bold mb-1" style={{color: '#1E3A5F'}}>
                {matches.filter(m => m.status === 'connected').length}
              </div>
              <div className="text-sm text-gray-600">Active Connections</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setActiveTab('intents')}
                className={`flex-1 px-6 py-4 font-semibold transition-all ${
                  activeTab === 'intents'
                    ? 'border-b-2 text-teal-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={activeTab === 'intents' ? {borderColor: '#14B8A6', color: '#14B8A6'} : {}}
              >
                My CSR Intents ({myIntents.length})
              </button>
              <button
                onClick={() => setActiveTab('projects')}
                className={`flex-1 px-6 py-4 font-semibold transition-all ${
                  activeTab === 'projects'
                    ? 'border-b-2 text-teal-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={activeTab === 'projects' ? {borderColor: '#14B8A6', color: '#14B8A6'} : {}}
              >
                Browse Projects ({projects.length})
              </button>
              <button
                onClick={() => setActiveTab('matches')}
                className={`flex-1 px-6 py-4 font-semibold transition-all ${
                  activeTab === 'matches'
                    ? 'border-b-2 text-teal-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={activeTab === 'matches' ? {borderColor: '#14B8A6', color: '#14B8A6'} : {}}
              >
                My Matches ({matches.length})
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {/* My Intents Tab */}
              {activeTab === 'intents' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold" style={{color: '#1E3A5F'}}>My CSR Intents</h3>
                    <button
                      onClick={handleCreateIntent}
                      className="px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all hover:shadow-lg"
                      style={{background: '#14B8A6', color: '#FFFFFF'}}
                    >
                      <Plus size={20} />
                      Post New Intent
                    </button>
                  </div>

                  {myIntents.length === 0 ? (
                    <div className="text-center py-12">
                      <Target className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600 mb-4">No intents posted yet</p>
                      <button
                        onClick={handleCreateIntent}
                        className="px-6 py-3 rounded-lg font-semibold"
                        style={{background: '#14B8A6', color: '#FFFFFF'}}
                      >
                        Post Your First Intent
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {myIntents.map((intent) => (
                        <div key={intent.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                          <h4 className="text-lg font-bold mb-2" style={{color: '#1E3A5F'}}>{intent.title}</h4>
                          <p className="text-gray-600 mb-4">{intent.summary}</p>
                          <div className="flex flex-wrap gap-4 text-sm">
                            <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                              {intent.sectors.join(', ')}
                            </span>
                            <span className="text-gray-600">📍 {intent.state}</span>
                            <span className="text-gray-600">💰 ₹{intent.budgetMin}L - ₹{intent.budgetMax}L</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Browse Projects Tab */}
              {activeTab === 'projects' && (
                <div>
                  <h3 className="text-xl font-bold mb-6" style={{color: '#1E3A5F'}}>Available NGO Projects</h3>

                  {projects.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600">No projects available at the moment</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {projects.map((project) => (
                        <div key={project.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <h4 className="text-lg font-bold mb-1" style={{color: '#1E3A5F'}}>{project.title}</h4>
                              <p className="text-sm text-gray-500">by {project.orgName}</p>
                            </div>
                            <button
                              onClick={() => handleExpressInterest(project.id)}
                              className="px-4 py-2 rounded-lg font-semibold flex items-center gap-2 transition-all hover:shadow-lg"
                              style={{background: '#14B8A6', color: '#FFFFFF'}}
                            >
                              <Heart size={16} />
                              Express Interest
                            </button>
                          </div>
                          <p className="text-gray-600 mb-4">{project.summary}</p>
                          <div className="flex flex-wrap gap-4 text-sm">
                            <span className="px-3 py-1 rounded-full bg-teal-100 text-teal-800">{project.sector}</span>
                            <span className="text-gray-600">📍 {project.state}</span>
                            <span className="text-gray-600">💰 ₹{project.fundingMin}L - ₹{project.fundingMax}L</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* My Matches Tab */}
              {activeTab === 'matches' && (
                <div>
                  <h3 className="text-xl font-bold mb-6" style={{color: '#1E3A5F'}}>My Matches</h3>

                  {matches.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600">No matches yet. Express interest in projects to get started!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {matches.map((match) => {
                        const badge = getMatchStatusBadge(match.status);
                        return (
                          <div key={match.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="text-lg font-bold mb-1" style={{color: '#1E3A5F'}}>
                                  Match with {match.ngoName}
                                </h4>
                                <p className="text-sm text-gray-500">
                                  {match.projectTitle || match.intentTitle}
                                </p>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.style}`}>
                                {badge.label}
                              </span>
                            </div>
                            {match.score > 0 && (
                              <div className="mb-3">
                                <div className="flex items-center justify-between text-sm mb-1">
                                  <span className="text-gray-600">Match Score</span>
                                  <span className="font-semibold" style={{color: '#14B8A6'}}>{match.score}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="h-2 rounded-full"
                                    style={{background: '#14B8A6', width: `${match.score}%`}}
                                  ></div>
                                </div>
                              </div>
                            )}
                            <p className="text-sm text-gray-600">
                              {match.status === 'pending_admin' && 'Awaiting TMG admin review. You will be notified once the introduction is made.'}
                              {match.status === 'connected' && 'TMG has facilitated the introduction. Check your email for contact details.'}
                              {match.status === 'closed' && 'This match has been closed.'}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorporateDashboard;
